import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { meetingAPI, bodyAPI } from '../api';
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { format } from 'date-fns';
import { CalendarIcon, Loader2, ArrowLeft, MapPin, Video, Clock, AlertCircle } from 'lucide-react';
import { cn } from '../lib/utils';

const MeetingForm = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedType = searchParams.get('type');

  const [bodies, setBodies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [bodiesLoading, setBodiesLoading] = useState(true);
  const [date, setDate] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    meeting_type: preselectedType || 'board',
    body_id: '',
    time: '10:00',
    venue: '',
    virtual_link: '',
    basis: 'regular',
    internal_notes: '',
  });

  useEffect(() => {
    fetchBodies();
  }, []);

  useEffect(() => {
    // Auto-select body based on meeting type
    if (bodies.length > 0 && formData.meeting_type) {
      const matchingBody = bodies.find(b => {
        if (formData.meeting_type === 'board') return b.body_type === 'board';
        if (formData.meeting_type === 'asm' || formData.meeting_type === 'ssm') return b.body_type === 'stockholders';
        return b.body_type.includes('committee');
      });
      if (matchingBody && !formData.body_id) {
        setFormData(prev => ({ ...prev, body_id: matchingBody.id }));
      }
    }
  }, [bodies, formData.meeting_type]);

  const fetchBodies = async () => {
    try {
      const response = await bodyAPI.getAll();
      setBodies(response.data);
    } catch (err) {
      console.error('Failed to fetch bodies:', err);
    } finally {
      setBodiesLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!date || !formData.body_id) return;

    setLoading(true);

    try {
      // Combine date and time
      const [hours, minutes] = formData.time.split(':');
      const dateTime = new Date(date);
      dateTime.setHours(parseInt(hours), parseInt(minutes), 0, 0);

      const meetingData = {
        title: formData.title,
        meeting_type: formData.meeting_type,
        body_id: formData.body_id,
        date_time: dateTime.toISOString(),
        venue: formData.venue || null,
        virtual_link: formData.virtual_link || null,
        basis: formData.basis,
        internal_notes: formData.internal_notes || null,
        organization_id: user.organization_id,
      };

      const response = await meetingAPI.create(meetingData);
      navigate(`/meetings/${response.data.id}`);
    } catch (err) {
      console.error('Failed to create meeting:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredBodies = bodies.filter(body => {
    if (formData.meeting_type === 'board') return body.body_type === 'board';
    if (formData.meeting_type === 'asm' || formData.meeting_type === 'ssm') return body.body_type === 'stockholders';
    if (formData.meeting_type === 'committee') return body.body_type.includes('committee');
    return true;
  });

  return (
    <div className="max-w-2xl mx-auto" data-testid="meeting-form-page">
      {/* Header */}
      <div className="mb-6">
        <Button 
          variant="ghost" 
          onClick={() => navigate('/meetings')}
          className="mb-4 text-[#5A6570] hover:text-[#1A1D21]"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Meetings
        </Button>
        <h1 className="text-2xl font-bold text-[#1A1D21]">Schedule New Meeting</h1>
        <p className="text-[#5A6570] mt-1">Create a new board, committee, or stockholders meeting</p>
      </div>

      <form onSubmit={handleSubmit}>
        <Card className="border-[#E2E6EA]">
          <CardContent className="p-6 space-y-6">
            {/* Meeting Type */}
            <div className="space-y-2">
              <Label>Meeting Type</Label>
              <Select
                value={formData.meeting_type}
                onValueChange={(value) => handleChange('meeting_type', value)}
              >
                <SelectTrigger data-testid="meeting-type-select" className="border-[#E2E6EA]">
                  <SelectValue placeholder="Select meeting type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="board">Board Meeting</SelectItem>
                  <SelectItem value="committee">Committee Meeting</SelectItem>
                  <SelectItem value="asm">Annual Stockholders Meeting (ASM)</SelectItem>
                  <SelectItem value="ssm">Special Stockholders Meeting (SSM)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Body */}
            <div className="space-y-2">
              <Label>Body/Committee</Label>
              <Select
                value={formData.body_id}
                onValueChange={(value) => handleChange('body_id', value)}
                disabled={bodiesLoading}
              >
                <SelectTrigger data-testid="body-select" className="border-[#E2E6EA]">
                  <SelectValue placeholder={bodiesLoading ? "Loading..." : "Select body"} />
                </SelectTrigger>
                <SelectContent>
                  {filteredBodies.map((body) => (
                    <SelectItem key={body.id} value={body.id}>
                      {body.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!bodiesLoading && filteredBodies.length === 0 && (
                <p className="text-xs text-amber-600">
                  No body configured for this meeting type. Go to Admin to create one.
                </p>
              )}
            </div>

            {/* Title */}
            <div className="space-y-2">
              <Label htmlFor="title">Meeting Title</Label>
              <Input
                id="title"
                placeholder="e.g., Regular Board Meeting - Q1 2026"
                value={formData.title}
                onChange={(e) => handleChange('title', e.target.value)}
                required
                data-testid="meeting-title-input"
                className="border-[#E2E6EA]"
              />
            </div>

            {/* Date and Time */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal border-[#E2E6EA]",
                        !date && "text-muted-foreground"
                      )}
                      data-testid="meeting-date-picker"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {date ? format(date, "PPP") : "Pick a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={setDate}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-2">
                <Label htmlFor="time">Time</Label>
                <div className="relative">
                  <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[#5A6570]" />
                  <Input
                    id="time"
                    type="time"
                    value={formData.time}
                    onChange={(e) => handleChange('time', e.target.value)}
                    className="pl-10 border-[#E2E6EA]"
                    data-testid="meeting-time-input"
                  />
                </div>
              </div>
            </div>

            {/* Basis */}
            <div className="space-y-2">
              <Label>Meeting Basis</Label>
              <Select
                value={formData.basis}
                onValueChange={(value) => handleChange('basis', value)}
              >
                <SelectTrigger data-testid="meeting-basis-select" className="border-[#E2E6EA]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="regular">Regular</SelectItem>
                  <SelectItem value="special">Special</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* RCC Notice Period Info */}
            {date && (
              <div className="p-3 bg-[#00A3A3]/5 border border-[#00A3A3]/20 rounded-lg" data-testid="notice-period-info">
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 text-[#00A3A3] flex-shrink-0 mt-0.5" />
                  <div className="text-xs text-[#3A3F45]">
                    <p className="font-medium mb-1">RCC Notice Requirement</p>
                    {(() => {
                      const mt = formData.meeting_type;
                      const basis = formData.basis;
                      let days = 7;
                      let ref = 'RCC default';
                      if (mt === 'asm') { days = 21; ref = 'RCC Sec 50'; }
                      else if (mt === 'ssm') { days = basis === 'special' ? 7 : 21; ref = 'RCC Sec 50'; }
                      else if (mt === 'board') { days = 7; ref = 'RCC Sec 53'; }
                      
                      const noticeDeadline = new Date(date);
                      noticeDeadline.setDate(noticeDeadline.getDate() - days);
                      const today = new Date();
                      today.setHours(0,0,0,0);
                      const isCompliant = noticeDeadline >= today;
                      
                      return (
                        <>
                          <p>Minimum <strong>{days}-day</strong> notice required ({ref})</p>
                          <p>
                            Notices must be sent by: <strong>{format(noticeDeadline, 'MMMM d, yyyy')}</strong>
                            {isCompliant ? (
                              <span className="ml-2 text-green-600 font-medium">Compliant</span>
                            ) : (
                              <span className="ml-2 text-red-600 font-medium">Deadline passed</span>
                            )}
                          </p>
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>
            )}

            {/* Venue */}
            <div className="space-y-2">
              <Label htmlFor="venue">Venue (Optional)</Label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[#5A6570]" />
                <Input
                  id="venue"
                  placeholder="e.g., Conference Room A, 5th Floor"
                  value={formData.venue}
                  onChange={(e) => handleChange('venue', e.target.value)}
                  className="pl-10 border-[#E2E6EA]"
                  data-testid="meeting-venue-input"
                />
              </div>
            </div>

            {/* Virtual Link */}
            <div className="space-y-2">
              <Label htmlFor="virtual_link">Virtual Meeting Link (Optional)</Label>
              <div className="relative">
                <Video className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[#5A6570]" />
                <Input
                  id="virtual_link"
                  placeholder="e.g., https://zoom.us/j/..."
                  value={formData.virtual_link}
                  onChange={(e) => handleChange('virtual_link', e.target.value)}
                  className="pl-10 border-[#E2E6EA]"
                  data-testid="meeting-virtual-link-input"
                />
              </div>
            </div>

            {/* Internal Notes */}
            <div className="space-y-2">
              <Label htmlFor="internal_notes">Internal Notes (Optional)</Label>
              <Textarea
                id="internal_notes"
                placeholder="Any internal notes or reminders..."
                value={formData.internal_notes}
                onChange={(e) => handleChange('internal_notes', e.target.value)}
                className="border-[#E2E6EA] min-h-[100px]"
                data-testid="meeting-notes-input"
              />
            </div>
          </CardContent>

          <CardFooter className="px-6 py-4 bg-[#F8F9FA] border-t border-[#E2E6EA] flex flex-col gap-3">
            {/* Validation hints */}
            {(!formData.title || !date || !formData.body_id) && (
              <div className="w-full text-sm text-[#5A6570] flex flex-wrap gap-x-4 gap-y-1">
                {!formData.title && <span className="flex items-center gap-1"><AlertCircle className="h-3 w-3 text-amber-500" />Title required</span>}
                {!date && <span className="flex items-center gap-1"><AlertCircle className="h-3 w-3 text-amber-500" />Date required</span>}
                {!formData.body_id && <span className="flex items-center gap-1"><AlertCircle className="h-3 w-3 text-amber-500" />Body required</span>}
              </div>
            )}
            <div className="w-full flex justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/meetings')}
                className="border-[#E2E6EA]"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-[#00A3A3] hover:bg-[#008585]"
                disabled={loading || !date || !formData.body_id || !formData.title}
                data-testid="create-meeting-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Create Meeting'
                )}
              </Button>
            </div>
          </CardFooter>
        </Card>
      </form>
    </div>
  );
};

export default MeetingForm;
